<template>
  <div class="ProjectDashboard">
    <YesNoDialog
      v-model="deleteProjectDialog"
      :title="$t('deleteProject')"
      :text="$t('askDeleteProj')"
      @yes="deleteProject()"
    />

    <header class="ProjectDashboard__masthead">
      <div class="ProjectDashboard__title-block">
        <v-app-bar-nav-icon class="ProjectDashboard__nav-toggle" @click="showDrawer()" />
        <div>
          <span class="zp-overline">{{ $t('projectStream') }}</span>
          <h1 class="ProjectDashboard__title">{{ $t('settings') }}</h1>
          <p class="ProjectDashboard__subtitle">{{ $t('dashboardSettingsSubtitle') }}</p>
        </div>
      </div>
    </header>

    <div class="ProjectDashboard__menu-row">
      <DashboardMenu
        class="ProjectDashboard__menu"
        :project-id="projectId"
        :project-type="projectType"
        :can-update-project="true"
      />
    </div>

    <main class="ProjectDashboard__content ProjectDashboard__content--narrow">
      <section class="ProjectDashboard__panel project-settings-form">
        <div class="ProjectDashboard__panel-head">
          <div>
            <span class="zp-overline">{{ $t('general_settings') }}</span>
            <h2>{{ $t('general_settings') }}</h2>
          </div>
        </div>
        <div class="ProjectDashboard__panel-body">
        <div>
          <ProjectForm
            :item-id="projectId"
            ref="form"
            @error="onError"
            @save="onSave"
            :system-info="systemInfo"
          />
        </div>

        <div class="ProjectDashboard__actions">
          <v-btn
            color="blue-grey"
            @click="sendTestNotification()"
            min-width="170"
            :disabled="testNotificationProgress"
            data-testid="settings-testAlerts"
          >{{ $t('testAlerts') }}</v-btn>
          <v-btn color="primary" @click="saveProject()">{{ $t('save') }}</v-btn>
        </div>

        <v-progress-linear
          v-if="testNotificationProgress"
          color="blue-grey darken-1"
          indeterminate
          rounded
          width="170"
          height="36"
          class="ProjectDashboard__progress ProjectDashboard__progress--fixed"
        ></v-progress-linear>
        </div>
      </section>

      <section class="ProjectDashboard__panel project-settings-danger">
        <div class="ProjectDashboard__panel-head">
          <div>
            <span class="zp-overline">{{ $t('danger_zone_settings') }}</span>
            <h2>{{ $t('danger_zone_settings') }}</h2>
          </div>
        </div>
        <div class="ProjectDashboard__panel-body">
      <div class="project-settings-button" v-if="projectType === ''">
        <div class="ProjectDashboard__action-row">
          <div class="ProjectDashboard__action-control">
            <v-btn
              color="primary"
              @click="backupProject"
              :disabled="backupProgress"
              min-width="170"
              data-testid="settings-exportProject"
            >{{ $t('backup') }}
            </v-btn>

            <v-progress-linear
              v-if="backupProgress"
              color="primary accent-4"
              indeterminate
              rounded
              height="36"
              class="ProjectDashboard__progress"
            ></v-progress-linear>

          </div>
          <div class="ProjectDashboard__action-copy">
              {{ $t('downloadTheProjectBackupFile') }}
          </div>
        </div>
      </div>

      <div class="project-settings-button" v-if="projectType === ''">
        <div class="ProjectDashboard__action-row">
          <div class="ProjectDashboard__action-control">
            <v-btn
              color="blue-grey"
              @click="clearCache"
              :disabled="clearCacheProgress"
              min-width="170"
              data-testid="settings-clearCache"
            >{{ $t('clear_cache') }}</v-btn>

            <v-progress-linear
              v-if="clearCacheProgress"
              color="blue-grey darken-1"
              indeterminate
              rounded
              height="36"
              style="margin-top: -36px"
            ></v-progress-linear>

          </div>
          <div class="ProjectDashboard__action-copy">
              {{ $t('clear_cache_message') }}
          </div>
        </div>
      </div>

      <div class="project-settings-button">
        <div class="ProjectDashboard__action-row ProjectDashboard__action-row--danger">
          <div class="ProjectDashboard__action-control">
            <v-btn
              color="error"
              min-width="170"
              @click="deleteProjectDialog = true"
              data-testid="settings-deleteProject"
            >{{ $t('deleteProject2') }}
            </v-btn>
          </div>
          <div class="ProjectDashboard__action-copy ProjectDashboard__action-copy--danger">
              {{ $t('onceYouDeleteAProjectThereIsNoGoingBackPleaseBeCer') }}
          </div>
        </div>
      </div>
        </div>
      </section>
    </main>
  </div>
</template>
<script>
import EventBus from '@/event-bus';
import ProjectForm from '@/components/ProjectForm.vue';
import { getErrorMessage } from '@/lib/error';
import axios from 'axios';
import YesNoDialog from '@/components/YesNoDialog.vue';
import delay from '@/lib/delay';
import DashboardMenu from '@/components/DashboardMenu.vue';

export default {
  components: { DashboardMenu, YesNoDialog, ProjectForm },
  props: {
    projectId: Number,
    projectType: String,
    systemInfo: Object,
  },

  data() {
    return {
      deleteProjectDialog: null,
      backupProgress: false,
      clearCacheProgress: false,
      testNotificationProgress: false,
    };
  },

  methods: {
    async sendTestNotification() {
      this.testNotificationProgress = true;
      try {
        await axios({
          method: 'post',
          url: `/api/project/${this.projectId}/notifications/test`,
          responseType: 'json',
        });
        EventBus.$emit('i-snackbar', {
          color: 'success',
          text: 'Test notification sent.',
        });
      } catch (err) {
        let msg;
        if (err.response.status === 409) {
          msg = 'Please allow alerts for the project and save it.';
        } else {
          msg = getErrorMessage(err);
        }

        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: msg,
        });
      } finally {
        this.testNotificationProgress = false;
      }
    },

    showDrawer() {
      EventBus.$emit('i-show-drawer');
    },

    onError(e) {
      EventBus.$emit('i-snackbar', {
        color: 'error',
        text: e.message,
      });
    },

    onSave(e) {
      EventBus.$emit('i-project', {
        action: 'edit',
        item: e.item,
      });
    },

    async saveProject() {
      await this.$refs.form.save();
    },

    async clearCache() {
      this.clearCacheProgress = true;
      await delay(1000);

      try {
        await axios({
          method: 'delete',
          url: `/api/project/${this.projectId}/cache`,
          transformResponse: (res) => res, // Necessary to not parse json
          responseType: 'json',
        });

        await delay(1000);

        EventBus.$emit('i-snackbar', {
          color: 'success',
          text: 'Project cache cleaned.',
        });
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(err),
        });
      } finally {
        this.clearCacheProgress = false;
      }
    },

    async backupProject() {
      this.backupProgress = true;
      await delay(1000);

      try {
        const backup = await axios({
          method: 'get',
          url: `/api/project/${this.projectId}/backup`,
          transformResponse: (res) => res, // Necessary to not parse json
          responseType: 'json',
        });

        const a = document.createElement('a');
        const blob = new Blob([backup.data], { type: 'application/json' });
        a.download = `backup_${this.projectId}_${Date.now()}.json`;
        a.href = URL.createObjectURL(blob);
        a.click();

        await delay(1000);

        EventBus.$emit('i-snackbar', {
          color: 'success',
          text: 'Project exported.',
        });
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(err),
        });
      } finally {
        this.backupProgress = false;
      }
    },

    async deleteProject() {
      try {
        await axios({
          method: 'delete',
          url: `/api/project/${this.projectId}`,
          responseType: 'json',
        });
        EventBus.$emit('i-project', {
          action: 'delete',
          item: {
            id: this.projectId,
          },
        });
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(err),
        });
      }
    },
  },
};
</script>
